import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Sharp Material Design Icons as SVG components
const MailIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 4H2v16h20V4zM4 6l8 5 8-5v2l-8 5-8-5V6z"/>
  </svg>
);

const MessageIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 2H4L2 4v18l4-4h14V2z"/>
  </svg>
);

const PresentationIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 22h12l-6-6zM23 3H1v16h4v-2H3V5h18v12h-2v2h4V3z"/>
  </svg>
);

const FileTextIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 2H4v20h16V8l-6-6zm4 18H6V4h7v5h5v11z"/>
  </svg>
);

const SheetIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 10h5v11h-5zm7 11h5V10h-5zM22 3H2v5h20zM2 10v11h5V10z"/>
  </svg>
);

const AssessmentIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 3H3v18h18V3zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
  </svg>
);

const VideoIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17 10.5V6H3v12h14v-4.5l4 4v-11l-4 4z"/>
  </svg>
);

const UserIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 3H3v18h18V3zm-9 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm6 10H6v-1c0-2 4-3.1 6-3.1s6 1.1 6 3.1v1z"/>
  </svg>
);

// Logo will be handled as an image

interface Connection {
  id: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  delay: number;
  duration: number;
  fromNodeIndex?: number; // undefined means from logomark
  toNodeIndex: number;
  timestamp: number;
}



const NODE_POSITIONS: { x: number; y: number }[] = [
  {x: 140, y: 120}, {x: 280, y: 100}, {x: 430, y: 140}, {x: 180, y: 220}, 
  {x: 380, y: 200}, {x: 120, y: 300}, {x: 320, y: 320}, {x: 480, y: 280}, 
  {x: 100, y: 200}, {x: 220, y: 80}, {x: 500, y: 200}, {x: 180, y: 360}, 
  {x: 400, y: 80}, {x: 80, y: 240}, {x: 460, y: 360}, {x: 340, y: 60}, 
  {x: 160, y: 160}, {x: 420, y: 260}
];

const ICONS = [MailIcon, MessageIcon, PresentationIcon, FileTextIcon, SheetIcon, AssessmentIcon, VideoIcon, UserIcon];
const CENTER = { x: 300, y: 200 };
const MIN_DISTANCE = 85;
const CENTER_BUFFER = 85; // 35px + 50px buffer
const MAX_CONNECTION_DISTANCE = 280;

export const KnowledgeGraphAnimation: React.FC = () => {
  const [visibleNodeIndices, setVisibleNodeIndices] = useState<number[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);

  // Calculate distance between two points
  const getDistance = useCallback((p1: { x: number; y: number }, p2: { x: number; y: number }) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }, []);

  // Check if a position is valid
  const isValidPosition = useCallback((nodeIndex: number, currentIndices: number[]) => {
    const position = NODE_POSITIONS[nodeIndex];
    
    if (getDistance(position, CENTER) < CENTER_BUFFER) {
      return false;
    }

    for (const existingIndex of currentIndices) {
      const existingPosition = NODE_POSITIONS[existingIndex];
      if (getDistance(position, existingPosition) < MIN_DISTANCE) {
        return false;
      }
    }

    return true;
  }, [getDistance]);

  // Create connection from logomark to node
  const createLogomarkConnection = useCallback((toNodeIndex: number, delay: number = 0) => {
    const toPosition = NODE_POSITIONS[toNodeIndex];
    const fromPosition = {
      x: CENTER.x + (toPosition.x - CENTER.x) / getDistance(CENTER, toPosition) * 35,
      y: CENTER.y + (toPosition.y - CENTER.y) / getDistance(CENTER, toPosition) * 35
    };

    const connection: Connection = {
      id: `logo-${toNodeIndex}-${Date.now()}-${Math.random()}`,
      from: fromPosition,
      to: toPosition,
      delay,
      duration: 0.3,
      fromNodeIndex: undefined,
      toNodeIndex,
      timestamp: Date.now()
    };

    setConnections(prev => [...prev, connection]);

    // Trigger reaction when connection is halfway complete
    setTimeout(() => {
      if (!activeReactions.current.has(toNodeIndex)) {
        setTimeout(() => triggerNodeReaction(toNodeIndex, 0), 0);
      }
    }, (delay + 0.15) * 1000);
  }, [getDistance]);



  // Track active reactions to prevent infinite loops
  const activeReactions = useRef(new Set<number>());

  // Trigger reaction from a node (shoot 2-3 lines to other nodes)
  const triggerNodeReaction = useCallback((sourceNodeIndex: number, delay: number) => {
    setTimeout(() => {
      setVisibleNodeIndices(currentVisibleNodes => {
        // Check if source node still exists
        if (!currentVisibleNodes.includes(sourceNodeIndex)) return currentVisibleNodes;
        
        // Prevent multiple simultaneous reactions from the same node
        if (activeReactions.current.has(sourceNodeIndex)) return currentVisibleNodes;
        activeReactions.current.add(sourceNodeIndex);
        
        // Remove all connections FROM this node (cleanup previous reactions)
        setConnections(prev => prev.filter(conn => conn.fromNodeIndex !== sourceNodeIndex));
        
        // Find available target nodes (excluding the source)
        const availableTargets = currentVisibleNodes
          .filter(index => index !== sourceNodeIndex)
          .filter(index => getDistance(NODE_POSITIONS[index], NODE_POSITIONS[sourceNodeIndex]) < MAX_CONNECTION_DISTANCE)
          .sort((a, b) => getDistance(NODE_POSITIONS[a], NODE_POSITIONS[sourceNodeIndex]) - getDistance(NODE_POSITIONS[b], NODE_POSITIONS[sourceNodeIndex]))
          .slice(0, 2); // Reduce to 2 connections to prevent overcrowding

        // Create new connections with chain reactions
        availableTargets.forEach((targetIndex, i) => {
          const fromPosition = NODE_POSITIONS[sourceNodeIndex];
          const toPosition = NODE_POSITIONS[targetIndex];
          const delay = i * 0.2;

          const connection: Connection = {
            id: `reaction-${sourceNodeIndex}-${targetIndex}-${Date.now()}-${Math.random()}`,
            from: fromPosition,
            to: toPosition,
            delay,
            duration: 0.3,
            fromNodeIndex: sourceNodeIndex,
            toNodeIndex: targetIndex,
            timestamp: Date.now()
          };

          setConnections(prev => [...prev, connection]);

          // Trigger reaction when connection is halfway complete
          setTimeout(() => {
            if (!activeReactions.current.has(targetIndex)) {
              setTimeout(() => triggerNodeReaction(targetIndex, 0), 0);
            }
          }, (delay + 0.15) * 1000);
        });

        // Clear the active reaction flag after connections are created
        setTimeout(() => {
          activeReactions.current.delete(sourceNodeIndex);
        }, 1000); // Longer delay to prevent rapid re-triggering
        
        return currentVisibleNodes; // Return unchanged
      });
    }, delay * 1000);
  }, [getDistance]);



  // Initialize with 9 nodes
  const initializeNodes = useCallback(() => {
    const indices: number[] = [];
    const maxAttempts = 100;
    let attempts = 0;

    while (indices.length < 9 && attempts < maxAttempts) {
      const randomIndex = Math.floor(Math.random() * NODE_POSITIONS.length);
      
      if (!indices.includes(randomIndex) && isValidPosition(randomIndex, indices)) {
        indices.push(randomIndex);
      }
      attempts++;
    }

    setVisibleNodeIndices(indices);

    // Start the reaction immediately after nodes are set
    setTimeout(() => {
      // Create initial 3 diverse connections from logomark
      const nodesWithAngles = indices.map(index => {
        const pos = NODE_POSITIONS[index];
        const angle = Math.atan2(pos.y - CENTER.y, pos.x - CENTER.x);
        return { index, angle };
      }).sort((a, b) => a.angle - b.angle);

      const selectedNodes: number[] = [];
      if (nodesWithAngles.length >= 3) {
        selectedNodes.push(nodesWithAngles[0].index);
        
        // Find node roughly 120 degrees away
        const targetAngle1 = nodesWithAngles[0].angle + (2 * Math.PI / 3);
        let bestNode1 = nodesWithAngles[1];
        let minDiff1 = Math.abs(bestNode1.angle - targetAngle1);
        
        for (let i = 1; i < nodesWithAngles.length; i++) {
          const diff = Math.abs(nodesWithAngles[i].angle - targetAngle1);
          if (diff < minDiff1) {
            minDiff1 = diff;
            bestNode1 = nodesWithAngles[i];
          }
        }
        selectedNodes.push(bestNode1.index);
        
        // Find node roughly 240 degrees away
        const targetAngle2 = nodesWithAngles[0].angle + (4 * Math.PI / 3);
        let bestNode2 = nodesWithAngles[2];
        let minDiff2 = Math.abs(bestNode2.angle - targetAngle2);
        
        for (let i = 1; i < nodesWithAngles.length; i++) {
          if (nodesWithAngles[i].index === bestNode1.index) continue;
          const diff = Math.abs(nodesWithAngles[i].angle - targetAngle2);
          if (diff < minDiff2) {
            minDiff2 = diff;
            bestNode2 = nodesWithAngles[i];
          }
        }
        selectedNodes.push(bestNode2.index);
      }

      // Create staggered initial connections
      selectedNodes.forEach((nodeIndex, i) => {
        createLogomarkConnection(nodeIndex, i * 0.3);
      });
    }, 100); // Small delay to ensure nodes are rendered first
  }, [isValidPosition, createLogomarkConnection]);

  // Replace random node
  const replaceRandomNode = useCallback(() => {
    setVisibleNodeIndices(currentIndices => {
      if (currentIndices.length === 0) return currentIndices;

      const indexToReplace = Math.floor(Math.random() * currentIndices.length);
      const oldNodeIndex = currentIndices[indexToReplace];
      const newIndices = [...currentIndices];
      
      let attempts = 0;
      const maxAttempts = 50;
      
      while (attempts < maxAttempts) {
        const candidateIndex = Math.floor(Math.random() * NODE_POSITIONS.length);
        const tempIndices = newIndices.filter((_, i) => i !== indexToReplace);
        
        if (!newIndices.includes(candidateIndex) && isValidPosition(candidateIndex, tempIndices)) {
          newIndices[indexToReplace] = candidateIndex;
          
          // Clean up connections involving the old node
          setConnections(prev => prev.filter(conn => 
            conn.fromNodeIndex !== oldNodeIndex && conn.toNodeIndex !== oldNodeIndex
          ));
          
          // Create connection from logomark to new node
          setTimeout(() => {
            createLogomarkConnection(candidateIndex, 0);
          }, 200); // Small delay to ensure node appears first
          
          break;
        }
        attempts++;
      }

      return newIndices;
    });
  }, [isValidPosition, createLogomarkConnection]);



  // Optimized connection cleanup - combined with other operations
  const cleanupConnections = useCallback(() => {
    const now = Date.now();
    setConnections(prev => prev.filter(conn => {
      // Remove old connections
      if (now - conn.timestamp > 1000) return false;
      
      // Remove connections to non-existent nodes
      if (!visibleNodeIndices.includes(conn.toNodeIndex)) return false;
      if (conn.fromNodeIndex !== undefined && !visibleNodeIndices.includes(conn.fromNodeIndex)) return false;
      
      return true;
    }));
  }, [visibleNodeIndices]);

  // Combined cleanup and logomark connections
  useEffect(() => {
    const interval = setInterval(() => {
      if (visibleNodeIndices.length === 0) return;
      
      // Clean up old connections
      cleanupConnections();
      
      // Create new logomark connection
      const randomNode = visibleNodeIndices[Math.floor(Math.random() * visibleNodeIndices.length)];
      createLogomarkConnection(randomNode, 0);
    }, 500);
    
    return () => clearInterval(interval);
  }, [visibleNodeIndices, createLogomarkConnection, cleanupConnections]);

  // Initialize on mount
  useEffect(() => {
    initializeNodes();
  }, [initializeNodes]);

  // Node replacement interval
  useEffect(() => {
    const interval = setInterval(replaceRandomNode, 3000); // Slower replacement for better flow
    return () => clearInterval(interval);
  }, [replaceRandomNode]);

  return (
    <div className="w-full h-full min-h-[400px] flex items-center justify-center">
      <svg
        viewBox="-20 -20 640 440"
        className="w-full h-full max-w-[600px] max-h-[400px]"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="knowledgeNodeGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4040FF" />
            <stop offset="100%" stopColor="#0000bd" />
          </radialGradient>
          <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0000bd" stopOpacity="0" />
            <stop offset="20%" stopColor="#0000bd" stopOpacity="0.6" />
            <stop offset="80%" stopColor="#4040FF" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#4040FF" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Render connections */}
        <AnimatePresence>
          {connections.map((connection) => (
            <motion.line
              key={connection.id}
              x1={connection.from.x}
              y1={connection.from.y}
              x2={connection.to.x}
              y2={connection.to.y}
              stroke="url(#connectionGradient)"
              strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                pathLength: { duration: connection.duration, delay: connection.delay, ease: "easeOut" },
                opacity: { duration: 0.3, delay: connection.delay }
              }}
            />
          ))}
        </AnimatePresence>

        {/* Render nodes */}
        <AnimatePresence mode="popLayout">
          {visibleNodeIndices.map((nodeIndex, i) => {
            const position = NODE_POSITIONS[nodeIndex];
            const IconComponent = ICONS[nodeIndex % ICONS.length];
            
            return (
              <motion.g
                key={nodeIndex}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{
                  duration: 0.3,
                  ease: "easeOut",
                  delay: i * 0.05
                }}
              >
                {/* Pulse ring */}
                <motion.circle
                  cx={position.x}
                  cy={position.y}
                  r="24"
                  fill="none"
                  stroke="#4040FF"
                  strokeWidth="2"
                  strokeOpacity="0.4"
                  animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeOut"
                  }}
                />
                
                {/* Node background */}
                <circle
                  cx={position.x}
                  cy={position.y}
                  r="24"
                  fill="url(#knowledgeNodeGradient)"
                />
                
                {/* Icon */}
                <foreignObject
                  x={position.x - 16}
                  y={position.y - 16}
                  width="32"
                  height="32"
                >
                  <div className="flex items-center justify-center w-full h-full text-white">
                    <IconComponent />
                  </div>
                </foreignObject>
              </motion.g>
            );
          })}
        </AnimatePresence>

        {/* Center logo */}
        <motion.g
          animate={{ scale: [1, 1.02, 1] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <image
            x={CENTER.x - 36}
            y={CENTER.y - 36}
            width="72"
            height="72"
            href="/LOGOmark.png"
            preserveAspectRatio="xMidYMid meet"
          />
        </motion.g>
      </svg>
    </div>
  );
}; 